import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';
import ChatChannelListContext from '../../context/ChatChannelListContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import ChatContext from '../../context/ChatContext';
import { ChatPage } from '../../utils/chatPage';
import LoadingPage from '../LoadingPage/LoadingPage';
import ChannelPreview from '../ChannelPreview';
import EmptyView from '../EmptyView';
import ChannelActionMenu from '../ChannelPreview/ChannelActionMenu';
import { ChannelBuckets } from '../../utils/channelBuckets';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ChatChannel from '../../Model/ChatChannel';
import LabelCheckbox from '../LabelCheckbox';
import ChannelBucketSelect from './ChannelBucketSelect';
import ChannelSort from './ChannelSort';
import ChannelSearch from './ChannelSearch';
import {
  ChannelListMenu,
  Container,
  ListItemsContainer,
  LoadMoreButton,
  ChannelListMenuActions,
  ArchiveButton,
  CheckboxRow,
} from './styles';
import texts from './texts.json';

const ListActions = {
  ARCHIVE_ALL: 'ARCHIVE_ALL',
  ARCHIVE_SELECTED: 'ARCHIVE_SELECTED',
};

const ListActionContent = {
  [ListActions.ARCHIVE_ALL]: {
    title: 'Archive All Chats',
    content: 'Are you sure you want to archive your chats?',
    confirm: 'Archive All',
    deny: 'No',
  },
  [ListActions.ARCHIVE_SELECTED]: {
    title: 'Archive Selected Chats',
    content: 'Are you sure you want to archive {count} chats?',
    confirm: 'Archive Chats',
    deny: 'No',
  },
};

const ChannelList = () => {
  const {
    setCurrentChatPage,
    readOnlyMode,
    authUser,
    sessionStore,
  } = useContext(ChatContext);

  const {
    channelList,
    channelUnreadCounts,
    isReady,
    loadMoreChannels,
    setActiveChannelId,
    isLoadingChannels,
    hasMoreChannels,
    selectedBucket,
    archiveChats,
    isRecentFirst,
    setIsRecentFirst,
    isCustomSortEnabled,
    setIsCustomSortEnabled,
  } = useContext(ChatChannelListContext);

  const { isCoachAssistant } = sessionStore;

  const [listAction, setListAction] = useState();
  const [selectedChats, setSelectedChats] = useState(new Set());
  const [showUnreadChats, setShowUnreadChats] = useState(false);
  const [isSenderNameUpdating, setIsSenderNameUpdating] = useState(false);

  const handleCustomSortChange = useCallback((checked) => {
    if (checked && !isRecentFirst) {
      setIsRecentFirst(true);
    }
    if (showUnreadChats) {
      setShowUnreadChats(false);
    }
    setIsCustomSortEnabled(checked);
  }, [isRecentFirst, showUnreadChats, setIsRecentFirst, setIsCustomSortEnabled]);

  const handleShowUnreadChats = useCallback((checked) => {
    setShowUnreadChats(checked);
    if (isCustomSortEnabled) {
      setIsCustomSortEnabled(false);
    }
  }, [isCustomSortEnabled, setIsCustomSortEnabled]);

  const sortedChannels = useMemo(() => {
    // Pinned channels always sit at the top regardless of custom sort.
    const { pinnedChannels, restChannels } = channelList.reduce(
      (acc, channel) => {
        (channel.isPinned ? acc.pinnedChannels : acc.restChannels).push(channel);
        return acc;
      },
      { pinnedChannels: [], restChannels: [] },
    );

    // No sorting will be applied for non AC users, and when custom sorting is disabled
    if (!isCoachAssistant || !isCustomSortEnabled) {
      return [...pinnedChannels, ...restChannels];
    }

    const reorderBuckets = {
      unreadSelfAssigned: [],
      unreadUnassigned: [],
      unreadAssigned: [],
      readSelfAssigned: [],
      readUnassigned: [],
      readAssigned: [],
    };

    // Add the chat channels to re-ordering buckets
    restChannels.forEach((channel) => {
      const {
        lastInteractionBy,
      } = channel;

      const isUnread = !!channelUnreadCounts[channel.id] && channelUnreadCounts[channel.id] > 0;
      const isSelfAssigned = !!lastInteractionBy && lastInteractionBy === authUser.id;
      const notInteracted = !lastInteractionBy;
      const isAssigned = !!lastInteractionBy && lastInteractionBy !== authUser.id;

      if (isUnread) {
        if (isSelfAssigned) {
          // Unread chat attached to the logged in user due to last interaction
          reorderBuckets.unreadSelfAssigned.push(channel);
        } else if (notInteracted) {
          // Unread chat unassigned to any user.
          reorderBuckets.unreadUnassigned.push(channel);
        } else if (isAssigned) {
          // Unread chat assigned to a different user
          reorderBuckets.unreadAssigned.push(channel);
        }
      } else if (isSelfAssigned) {
        // Read chat attached to the logged in user due to last interaction
        reorderBuckets.readSelfAssigned.push(channel);
      } else if (notInteracted) {
        // Read chat unassigned to any user.
        reorderBuckets.readUnassigned.push(channel);
      } else if (isAssigned) {
        // Read chat assigned to a different user
        reorderBuckets.readAssigned.push(channel);
      }
    });

    // Concatenate buckets in the specified order
    const sortedChannelList = [
      ...pinnedChannels,
      ...reorderBuckets.unreadSelfAssigned,
      ...reorderBuckets.unreadUnassigned,
      ...reorderBuckets.unreadAssigned,
      ...reorderBuckets.readSelfAssigned,
      ...reorderBuckets.readUnassigned,
      ...reorderBuckets.readAssigned,
    ];

    return sortedChannelList;
  }, [
    channelUnreadCounts,
    isCoachAssistant,
    isCustomSortEnabled,
    channelList,
    authUser.id,
  ]);

  const {
    setQuotedMessage,
    activeChannel,
  } = useContext(ChatChannelContext);

  const [anchorEl, setAnchorEl] = useState();
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [activePopperChannel, setActivePopperChannel] = useState();

  const handleAction = (event, newChannel) => {
    // This prevents calling the onClick event for the PreviewContainer component
    event.stopPropagation();
    setActivePopperChannel(newChannel);
    setAnchorEl(event.currentTarget);
    if (!isPopperOpen) {
      setIsPopperOpen((prev) => !prev);
    }
  };

  const handleChannelClick = (channelId) => {
    setActiveChannelId(channelId);
    setCurrentChatPage(ChatPage.SingleChannelView);
    setQuotedMessage();
  };

  const onChangeSenderNameVisibility = useCallback(async (checked) => {
    setIsSenderNameUpdating(true);
    try {
      await authUser?.updateShowACSenderNames(checked);
    } finally {
      setIsSenderNameUpdating(false);
    }
  }, [authUser]);

  const filteredChannels = useMemo(() => {
    if (showUnreadChats) {
      return sortedChannels.filter((channel) => !!channelUnreadCounts[channel.id]);
    }
    return sortedChannels;
  }, [sortedChannels, channelUnreadCounts, showUnreadChats]);

  const archiveSelected = useCallback(async () => {
    const channelsToArchive = [];
    selectedChats.forEach((index) => {
      const channel = filteredChannels[index];
      if (channel) {
        channelsToArchive.push(channel);
      }
    });

    await ChatChannel.archiveChats(channelsToArchive);
    setSelectedChats(new Set());
  }, [selectedChats, filteredChannels]);

  const enableBulkArchiving = isCoachAssistant;

  const handleChatSelection = useCallback((e, checked, index) => {
    const isShiftPressed = e.nativeEvent.shiftKey;

    if (!isShiftPressed) {
      return setSelectedChats((prev) => {
        const set = new Set(prev);
        if (checked) {
          set.add(index);
        } else {
          set.delete(index);
        }
        return set;
      });
    }

    return setSelectedChats((prev) => {
      const sortedSelectedIndexes = Array.from(prev).sort((a, b) => a - b);
      const newSet = new Set(prev);

      // If no previous selections, treat as normal click
      if (sortedSelectedIndexes.length === 0) {
        if (checked) {
          newSet.add(index);
        }
        return newSet;
      }

      // Find the closest selected index to create a range
      const lastSelectedIndex = sortedSelectedIndexes[sortedSelectedIndexes.length - 1];
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);

      // Add or remove all indexes in the range
      for (let i = start; i <= end; i++) {
        if (checked) {
          newSet.add(i);
        } else {
          newSet.delete(i);
        }
      }

      return newSet;
    });
  }, []);

  return (
    <Container>
      <ChannelListMenu>
        <ChannelBucketSelect />
        <ChannelSearch />
        <ChannelListMenuActions>
          <ChannelSort isCustomSortEnabled={isCustomSortEnabled} />
          {enableBulkArchiving && selectedBucket === ChannelBuckets.ACTIVE && (
            <ArchiveButton
              type="button"
              disabled={channelList.length === 0}
              onClick={() => {
                setListAction(selectedChats.size === 0 ? ListActions.ARCHIVE_ALL : ListActions.ARCHIVE_SELECTED);
              }}
            >
              {selectedChats.size === 0 ? 'Archive All' : 'Archive Selected'}
            </ArchiveButton>
          )}
        </ChannelListMenuActions>
        <CheckboxRow>
          {isCoachAssistant && (
            <LabelCheckbox
              label={texts.customSorting}
              onChange={handleCustomSortChange}
              isChecked={isCustomSortEnabled}
            />
          )}
          {selectedBucket !== ChannelBuckets.AI_FRIEND && (
            <LabelCheckbox
              label={texts.showUnreadChats}
              onChange={handleShowUnreadChats}
              isChecked={showUnreadChats}
            />
          )}
          {isCoachAssistant && (
            <LabelCheckbox
              label={texts.showSenderNames}
              disabled={isSenderNameUpdating}
              onChange={onChangeSenderNameVisibility}
              isChecked={!!authUser?.showACSenderNames}
            />
          )}
        </CheckboxRow>
      </ChannelListMenu>
      <ListItemsContainer>
        {isReady && filteredChannels.length === 0 && (
          <EmptyView content={texts.noChannels} />
        )}
        {isReady && filteredChannels.map((channel, index) => (
          <ChannelPreview
            listIndex={index}
            key={channel.id}
            onClick={() => handleChannelClick(channel.id)}
            image={channel.profileImage}
            lastMessage={{
              text: channel.lastMessageText,
              createdAt: channel.lastMessageAt,
            }}
            unreadCount={channelUnreadCounts[channel.id] || 0}
            name={channel.name}
            isAdmin={channel.isAdmin}
            isActive={channel.id === activeChannel?.id}
            shouldAllowActions={!readOnlyMode && selectedBucket !== ChannelBuckets.AI_FRIEND}
            isOnline={channel.isOnline}
            isUnread={channel.isUnread}
            handleAction={handleAction}
            channel={channel}
            showCheckbox={enableBulkArchiving && selectedBucket === ChannelBuckets.ACTIVE}
            checked={!!selectedChats.has(index)}
            checkboxOnChange={handleChatSelection}
          />
        ))}
        {hasMoreChannels && sortedChannels.length > 0 && (
          <LoadMoreButton onClick={() => loadMoreChannels()}>
            {texts.loadMore}
          </LoadMoreButton>
        )}
        {(isLoadingChannels || !isReady) && <LoadingPage fullHeight />}
        <ChannelActionMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          isPopperOpen={isPopperOpen}
          setIsPopperOpen={setIsPopperOpen}
          selectedBucket={selectedBucket}
          channel={activePopperChannel}
        />
      </ListItemsContainer>

      <ConfirmDialog
        isOpen={!!listAction}
        dialogTexts={{
          title: listAction ? ListActionContent[listAction].title : '',
          content: listAction ? format(ListActionContent[listAction].content, { count: selectedChats.size }) : '',
          cancel: listAction ? ListActionContent[listAction].deny : '',
          confirm: listAction ? ListActionContent[listAction].confirm : '',
        }}
        onCancel={() => setListAction(undefined)}
        onConfirm={() => {
          if (listAction === ListActions.ARCHIVE_ALL) {
            archiveChats();
          } else if (listAction === ListActions.ARCHIVE_SELECTED) {
            archiveSelected();
          }

          setListAction(undefined);
        }}
      />
    </Container>
  );
};

export default compose(
  observer,
)(ChannelList);
